<template>
    <VueFinalModal
        class="confirm-modal flex justify-center items-center px-4"
        content-class="confirm-modal-content max-w-lg"
        overlay-transition="vfm-fade"
        content-transition="vfm-fade"
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-modal-title"
    >
        <div class="bg-white rounded-lg overflow-hidden shadow-xl w-full">
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 id="download-modal-title" class="text-lg font-medium text-gray-900">Download Options</h3>
                <button
                    @click="handleClose"
                    class="text-gray-600 hover:text-gray-700"
                    aria-label="Close download options"
                >
                    <font-awesome :icon="['fas', 'x']" class="w-3.5 h-3.5" aria-hidden="true" />
                </button>
            </div>
            <div class="px-6 py-6">
                <div role="radiogroup" aria-labelledby="download-modal-title" class="space-y-3">
                    <label
                        v-if="showAccessibleOption"
                        class="flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors"
                        :class="selected === 'accessible' ? 'border-primary bg-primary-100' : 'border-gray-300 hover:bg-gray-100'"
                    >
                        <input
                            type="radio"
                            name="download-option"
                            value="accessible"
                            v-model="selected"
                            class="mt-0.5 h-4 w-4 accent-primary"
                            aria-describedby="accessible-desc"
                        />
                        <div>
                            <span class="font-semibold text-gray-900">Accessible Version</span>
                            <p id="accessible-desc" class="text-sm text-gray-600 mt-1">Enhanced for assistive technologies with improved structure and reading order. Results may vary based on the complexity of the original file.</p>
                        </div>
                    </label>
                    <label
                        class="flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors"
                        :class="selected === 'original' ? 'border-primary bg-primary-100' : 'border-gray-300 hover:bg-gray-100'"
                    >
                        <input
                            type="radio"
                            name="download-option"
                            value="original"
                            v-model="selected"
                            class="mt-0.5 h-4 w-4 accent-primary"
                            aria-describedby="original-desc"
                        />
                        <div>
                            <span class="font-semibold text-gray-900">Original File</span>
                            <p id="original-desc" class="text-sm text-gray-600 mt-1">The document as it was uploaded. Best for printing or when original formatting is important.</p>
                        </div>
                    </label>
                </div>
            </div>
            <div class="border-t border-gray-300">
                <div class="flex justify-end p-4 gap-2">
                    <Button type="secondary" button-type="button" @click="handleClose">
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        button-type="button"
                        :disabled="!selected || loading"
                        :loading="loading"
                        :aria-disabled="!selected || loading"
                        @click="handleDownload"
                    >
                        Download
                    </Button>
                </div>
            </div>
            <div aria-live="polite" class="sr-only">
                <span v-if="loading">Processing accessible PDF, please wait.</span>
            </div>
        </div>
    </VueFinalModal>
</template>

<script setup>
import { ref } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import Button from '~/components/ui/Button.vue';

const props = defineProps({
    loading: {
        type: Boolean,
        default: false
    },
    showAccessibleOption: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['close', 'download']);

// When the accessible option is hidden there's only one choice — preselect it
// so the user can just click Download.
const selected = ref(props.showAccessibleOption ? null : 'original');

const trackHeapEvent = (eventName, properties = {}) => {
    if (typeof window !== 'undefined' && window.heap?.track) {
        window.heap.track(eventName, properties);
    }
};

trackHeapEvent('PDF Download Modal Opened');

const handleDownload = () => {
    if (selected.value) {
        trackHeapEvent('PDF Download Modal Download', { version: selected.value });
        emit('download', selected.value);
    }
};

const handleClose = () => {
    trackHeapEvent('PDF Download Modal Cancelled');
    emit('close');
};
</script>
